import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';

const ASSET_PACKS_COLLECTION = 'assetPacks';

const normalizeDocIdSegment = (value) =>
  String(value || '')
    .trim()
    .replace(/[/?#[\]]/g, '_')
    .replace(/\s+/g, '_');

export const getTeamLogoUrl = (team = {}) =>
  team.logo || team.url || team.teamLogo || team.image || null;

export const getTeamAssetPackName = (team = {}) =>
  team.sourceTeamName || team.teamName || team.name || team.shortName || team.teamId || team.id || 'Imported Team';

export const getCommunityAssetPackId = (team = {}) => {
  const preferredId = team.id || team.sourceLibraryId || team.teamId || team.sourceTeam;
  const fallbackId = team.sourceTeamName || team.teamName || team.name || team.shortName || getTeamLogoUrl(team);
  return normalizeDocIdSegment(preferredId || fallbackId || 'imported_team');
};

export const normalizeCommunityAssetPlayers = (team = {}) => {
  let players = [];

  if (Array.isArray(team.players) && team.players.length > 0) {
    players = team.players;
  } else if (Array.isArray(team.roster) && team.roster.length > 0) {
    players = team.roster;
  } else if (Array.isArray(team.playerPics) && team.playerPics.length > 0) {
    players = team.playerPics.map((pic, idx) => ({
      name: `${getTeamAssetPackName(team)} #${idx + 1}`,
      avatar: pic,
    }));
  }

  return players.map((player, idx) => ({
    name: player?.name || player?.ign || `Player ${idx + 1}`,
    avatar: player?.avatar || player?.pic || player?.url || player?.image || '',
  }));
};

export const buildCommunityAssetPackPayload = (team = {}, importedBy = null) => {
  const packId = getCommunityAssetPackId(team);

  return {
    sourceLibraryId: team.id || team.sourceLibraryId || packId,
    sourceTeam: team.teamId || team.sourceTeam || null,
    sourceTeamName: getTeamAssetPackName(team),
    logo: getTeamLogoUrl(team),
    players: normalizeCommunityAssetPlayers(team),
    importedAt: serverTimestamp(),
    importedBy: importedBy || null,
    updatedAt: serverTimestamp(),
  };
};

export const getCommunityAssetPackRef = (teamId, packId) =>
  doc(db, `teams/${teamId}/${ASSET_PACKS_COLLECTION}/${packId}`);

const getLegacySelectedAssetsRef = (teamId) =>
  doc(db, `teams/${teamId}/assets/selected`);

const hasUsableLegacyAssets = (assets = {}) =>
  Boolean(getTeamLogoUrl(assets)) || normalizeCommunityAssetPlayers(assets).some((player) => !!player.avatar);

export const listCommunityAssetPacks = async (teamId) => {
  if (!teamId) return [];

  const snap = await getDocs(collection(db, `teams/${teamId}/${ASSET_PACKS_COLLECTION}`));
  return snap.docs.map((packDoc) => ({
    id: packDoc.id,
    ...packDoc.data(),
  }));
};

export const getCommunityAssetPack = async (teamId, packId) => {
  if (!teamId || !packId) return null;

  const snap = await getDoc(getCommunityAssetPackRef(teamId, packId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const addCommunityAssetPack = async (teamId, team, importedBy = null) => {
  if (!teamId) {
    throw new Error('teamId is required to add a community asset pack.');
  }

  const packId = getCommunityAssetPackId(team);
  const packRef = getCommunityAssetPackRef(teamId, packId);
  const payload = buildCommunityAssetPackPayload(team, importedBy);
  const existingPack = await getDoc(packRef);

  if (existingPack.exists()) {
    delete payload.importedAt;
  }

  await setDoc(packRef, payload, { merge: true });

  return {
    id: packId,
    ...payload,
  };
};

export const removeCommunityAssetPack = async (teamId, packId) => {
  if (!teamId || !packId) return;

  await deleteDoc(getCommunityAssetPackRef(teamId, packId));
};

export const migrateLegacySelectedAssetPack = async (teamId, importedBy = null) => {
  if (!teamId) {
    return { migrated: false, pack: null };
  }

  const legacyRef = getLegacySelectedAssetsRef(teamId);
  const legacySnap = await getDoc(legacyRef);

  if (!legacySnap.exists()) {
    return { migrated: false, pack: null };
  }

  const legacyData = legacySnap.data();
  if (!hasUsableLegacyAssets(legacyData)) {
    return { migrated: false, pack: null };
  }

  const packId = getCommunityAssetPackId(legacyData);
  const packRef = getCommunityAssetPackRef(teamId, packId);
  const payload = {
    ...buildCommunityAssetPackPayload(legacyData, importedBy),
    sourceLibraryId: legacyData.sourceLibraryId || packId,
    sourceTeam: legacyData.sourceTeam || legacyData.teamId || null,
    sourceTeamName: legacyData.sourceTeamName || getTeamAssetPackName(legacyData),
    migratedFrom: 'assets/selected',
    migratedAt: serverTimestamp(),
  };

  if (legacyData.importedAt) {
    payload.importedAt = legacyData.importedAt;
  }

  await setDoc(packRef, payload, { merge: true });

  const verifiedSnap = await getDoc(packRef);
  if (!verifiedSnap.exists()) {
    throw new Error('Legacy asset pack migration write could not be verified.');
  }

  return {
    migrated: true,
    pack: {
      id: packId,
      ...verifiedSnap.data(),
    },
  };
};
